/**
 * ShareRedeemView — landing page for the share-link Empfänger.
 *
 * Flow:
 *   1. Recipient klickt Link aus E-Mail → /redeem/:token
 *   2. We POST /api/v1/share/{token}/redeem (anonymous if no JWT in
 *      localStorage; authenticated if logged in).
 *   3a. Backend says `granted=true` → redirect to /post/<post_id>.
 *   3b. Backend says `requires_login=true` + email → show a card with
 *       "Du wurdest eingeladen — Login-Link wird an X gesendet"
 *       and trigger auth-api magic-link.
 *
 * Phase 3 follow-up: auto-trigger the magic-link send via auth-api.
 * For now we surface the email and a Button.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CONTENT_API } from '../config'
import { getAccessToken } from '../auth'

interface RedeemResponse {
  post_id: number
  permission: 'read' | 'write'
  requires_login: boolean
  email: string | null
  granted: boolean
}

export function ShareRedeemView() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'granted'; r: RedeemResponse }
    | { kind: 'needs-login'; r: RedeemResponse }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' })

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: 'Kein Share-Token in der URL' })
      return
    }
    void (async () => {
      try {
        const tok = getAccessToken()
        const r = await fetch(
          `${CONTENT_API}/api/v1/share/${encodeURIComponent(token)}/redeem`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
            },
            body: JSON.stringify({}),
          },
        )
        if (!r.ok) {
          const txt = await r.text()
          setState({
            kind: 'error',
            message: `HTTP ${r.status}: ${txt.slice(0, 200)}`,
          })
          return
        }
        const data = (await r.json()) as RedeemResponse
        if (data.granted) {
          setState({ kind: 'granted', r: data })
          // Auto-navigate after a short moment so the user sees the badge
          window.setTimeout(() => navigate(`/post/${data.post_id}`), 800)
        } else {
          setState({ kind: 'needs-login', r: data })
        }
      } catch (e) {
        setState({
          kind: 'error',
          message: String((e as Error).message || e),
        })
      }
    })()
  }, [token, navigate])

  // Phase 2: magic-link path is gone — instead we redirect to the
  // public /signup page with the current /redeem URL as next, plus a
  // pre-filled email when the grant carried one. After signup the
  // user comes back to /redeem/<token> with a valid JWT whose email
  // matches grant.email → post_permissions.match → granted.
  const goToSignup = (email: string | null) => {
    const next = encodeURIComponent(window.location.href)
    const emailQs = email ? `&email=${encodeURIComponent(email)}` : ''
    navigate(`/signup?next=${next}${emailQs}`)
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: '100%',
          padding: 28,
          borderRadius: 12,
          background: 'var(--surface, #fff)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        <h1 style={{ marginTop: 0, fontSize: 22 }}>Beitrags-Freigabe</h1>

        {state.kind === 'loading' && (
          <p style={{ color: 'var(--text-muted, #666)' }}>Prüfe Token…</p>
        )}

        {state.kind === 'granted' && (
          <div>
            <p
              style={{
                padding: 12,
                background: '#d4edda',
                color: '#155724',
                borderRadius: 6,
              }}
            >
              <strong>Zugang gewährt</strong> ({state.r.permission}). Du
              wirst weitergeleitet …
            </p>
          </div>
        )}

        {state.kind === 'needs-login' && (
          <div>
            <p style={{ fontSize: 15 }}>
              Du wurdest eingeladen den Beitrag mit{' '}
              <strong>{state.r.permission === 'write' ? 'Schreib' : 'Lese'}-Zugang</strong>{' '}
              zu öffnen.
            </p>
            {state.r.email ? (
              <>
                <p style={{ fontSize: 14, color: 'var(--text-muted, #666)' }}>
                  Die Einladung gilt für <strong>{state.r.email}</strong>.
                  Lege ein Konto an oder melde dich an um den Beitrag zu öffnen.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => goToSignup(state.r.email)}
                    style={{
                      padding: '10px 18px',
                      background: 'var(--accent, #4a6cf7)',
                      color: 'white',
                      border: 0,
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                  >
                    Konto erstellen
                  </button>
                  <button
                    onClick={() => navigate(`/login?next=${encodeURIComponent(window.location.href)}`)}
                    style={{
                      padding: '10px 18px',
                      background: 'transparent',
                      color: 'var(--accent, #4a6cf7)',
                      border: '1px solid var(--accent, #4a6cf7)',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                  >
                    Anmelden
                  </button>
                </div>
              </>
            ) : (
              <p style={{ fontSize: 14 }}>
                Bitte logge dich ein und öffne den Share-Link nochmal.
              </p>
            )}
          </div>
        )}

        {state.kind === 'error' && (
          <div
            style={{
              padding: 12,
              background: '#fee',
              color: '#900',
              borderRadius: 6,
            }}
          >
            {state.message}
          </div>
        )}
      </div>
    </div>
  )
}
